import { doc, setDoc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from './config';
import type { CheckIn, SponsorComment, NightlyReview, UserStreaks } from '../../types/checkin';

/**
 * Format a Date object to YYYY-MM-DD string for use as document IDs
 */
export function formatDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Save a morning check-in to Firestore
 * Document path: users/{userId}/checkins/{YYYY-MM-DD}
 * Uses merge: true to preserve any existing nightly review data
 * Automatically updates lastUpdatedAt and editCount
 */
export async function saveCheckIn(userId: string, checkIn: CheckIn): Promise<void> {
  try {
    const dateKey = formatDateKey(checkIn.date.toDate());
    const docRef = doc(db, 'users', userId, 'checkins', dateKey);

    // Always update timestamp and preserve editCount
    const checkInData = {
      ...checkIn,
      lastUpdatedAt: Timestamp.now(),  // Always update timestamp
      editCount: checkIn.editCount || 0,  // Default to 0 if new
    };

    await setDoc(
      docRef,
      {
        morning: checkInData,
      },
      { merge: true }
    );

    console.log(`Check-in saved successfully for ${dateKey}`);
  } catch (error) {
    console.error('Error saving check-in:', error);
    throw new Error(`Failed to save check-in: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Save a nightly review to Firestore
 * Document path: users/{userId}/checkins/{YYYY-MM-DD}
 * Uses merge: true to preserve the morning check-in data
 */
export async function saveNightlyReview(userId: string, review: NightlyReview): Promise<void> {
  try {
    const dateKey = formatDateKey(review.date.toDate());
    const docRef = doc(db, 'users', userId, 'checkins', dateKey);

    await setDoc(
      docRef,
      {
        nightly: {
          ...review,
          lastUpdatedAt: Timestamp.now(),
        },
      },
      { merge: true }
    );

    console.log(`Nightly review saved successfully for ${dateKey}`);
  } catch (error) {
    console.error('Error saving nightly review:', error);
    throw new Error(`Failed to save nightly review: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Load a check-in for a specific date
 * Returns the morning check-in or null if it doesn't exist
 */
export async function loadCheckIn(userId: string, date: Date): Promise<CheckIn | null> {
  try {
    const dateKey = formatDateKey(date);
    const docRef = doc(db, 'users', userId, 'checkins', dateKey);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    const morningData = data.morning;

    if (!morningData) {
      return null;
    }

    // Return the check-in data (Firestore already handles Timestamp conversion)
    return morningData as CheckIn;
  } catch (error) {
    console.error('Error loading check-in:', error);
    throw new Error(`Failed to load check-in: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Load today's check-ins
 * Returns an object with morning property (null if doesn't exist)
 */
export async function loadTodayCheckIns(userId: string): Promise<{ morning: CheckIn | null }> {
  try {
    const today = new Date();
    const morning = await loadCheckIn(userId, today);

    return { morning };
  } catch (error) {
    console.error('Error loading today\'s check-ins:', error);
    throw new Error(`Failed to load today's check-ins: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Load today's morning check-in for edit detection
 * Returns the morning check-in or null if it doesn't exist
 */
export async function loadTodayMorningCheckIn(
  userId: string
): Promise<CheckIn | null> {
  if (!auth.currentUser) return null;

  const dateKey = formatDateKey(new Date());
  const docRef = doc(db, 'users', userId, 'checkins', dateKey);

  try {
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.morning || null;
    }
    return null;
  } catch (error) {
    console.error('Error loading today\'s check-in:', error);
    return null;
  }
}

/**
 * Load today's nightly review
 * Returns the nightly review or null if it doesn't exist
 */
export async function getTodayNightlyReview(
  userId: string
): Promise<NightlyReview | null> {
  if (!auth.currentUser) return null;

  const dateKey = formatDateKey(new Date());
  const docRef = doc(db, 'users', userId, 'checkins', dateKey);

  try {
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.nightly || null;
    }
    return null;
  } catch (error) {
    console.error('Error loading today\'s nightly review:', error);
    return null;
  }
}

/**
 * Update check-in sharing settings retroactively
 * Allows users to share or unshare check-ins with sponsors after submission
 */
export async function updateCheckInSharing(
  userId: string,
  date: Date,
  type: 'morning' | 'nightly',
  sponsorIds: string[]
): Promise<void> {
  try {
    if (!auth.currentUser) {
      throw new Error('No authenticated user');
    }

    // Authorization: Users can only update their own check-ins
    if (auth.currentUser.uid !== userId) {
      throw new Error('Unauthorized: Cannot update another user\'s check-in sharing settings');
    }

    const dateKey = formatDateKey(date);
    const docRef = doc(db, 'users', userId, 'checkins', dateKey);

    // Verify document exists before updating
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      throw new Error(`Check-in not found for ${dateKey}`);
    }

    await updateDoc(docRef, {
      [`${type}.sharedWithSponsors`]: sponsorIds,
    });

    console.log(`Updated sharing for ${dateKey} ${type}`);
  } catch (error) {
    console.error('Error updating check-in sharing:', error);
    throw new Error(`Failed to update check-in sharing: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Add a sponsor comment to a protege's check-in
 * Sponsors can provide feedback on check-ins shared with them
 */
export async function addSponsorComment(
  protegeId: string,
  date: Date,
  type: 'morning' | 'nightly',
  comment: Omit<SponsorComment, 'id' | 'createdAt'>
): Promise<void> {
  try {
    if (!auth.currentUser) {
      throw new Error('No authenticated user');
    }

    // Authorization: Verify the authenticated user is the sponsor making the comment
    if (auth.currentUser.uid !== comment.sponsorId) {
      throw new Error('Unauthorized: Cannot add comments as another sponsor');
    }

    const dateKey = formatDateKey(date);
    const docRef = doc(db, 'users', protegeId, 'checkins', dateKey);

    // Verify document exists before updating
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      throw new Error(`Check-in not found for ${dateKey}`);
    }

    // Verify the check-in is shared with this sponsor
    const checkInData = docSnap.data();
    const sharedWithSponsors = checkInData[type]?.sharedWithSponsors || [];
    if (!sharedWithSponsors.includes(comment.sponsorId)) {
      throw new Error('Unauthorized: Check-in is not shared with this sponsor');
    }

    const commentId = crypto.randomUUID();

    const fullComment: SponsorComment = {
      ...comment,
      id: commentId,
      createdAt: Timestamp.now(),
    };

    await updateDoc(docRef, {
      [`${type}.sponsorComments.${commentId}`]: fullComment,
    });

    console.log(`Added comment to ${protegeId}'s ${type} check-in on ${dateKey}`);
  } catch (error) {
    console.error('Error adding sponsor comment:', error);
    throw new Error(`Failed to add sponsor comment: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Add a sponsor reaction to a protege's check-in
 * Quick way for sponsors to acknowledge check-ins
 */
export async function addSponsorReaction(
  protegeId: string,
  date: Date,
  type: 'morning' | 'nightly',
  sponsorId: string,
  reaction: '❤️' | '👍' | '🙏'
): Promise<void> {
  try {
    if (!auth.currentUser) {
      throw new Error('No authenticated user');
    }

    // Authorization: Verify the authenticated user is the sponsor adding the reaction
    if (auth.currentUser.uid !== sponsorId) {
      throw new Error('Unauthorized: Cannot add reactions as another sponsor');
    }

    const dateKey = formatDateKey(date);
    const docRef = doc(db, 'users', protegeId, 'checkins', dateKey);

    // Verify document exists before updating
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      throw new Error(`Check-in not found for ${dateKey}`);
    }

    // Verify the check-in is shared with this sponsor
    const checkInData = docSnap.data();
    const sharedWithSponsors = checkInData[type]?.sharedWithSponsors || [];
    if (!sharedWithSponsors.includes(sponsorId)) {
      throw new Error('Unauthorized: Check-in is not shared with this sponsor');
    }

    await updateDoc(docRef, {
      [`${type}.sponsorReactions.${sponsorId}`]: reaction,
    });

    console.log(`Added reaction to ${protegeId}'s ${type} check-in on ${dateKey}`);
  } catch (error) {
    console.error('Error adding sponsor reaction:', error);
    throw new Error(`Failed to add sponsor reaction: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Get user's check-in streaks
 * Returns streak data for morning, nightly, and full-day check-ins
 */
export async function getUserStreaks(userId: string): Promise<UserStreaks | null> {
  try {
    const docRef = doc(db, 'users', userId, 'streaks', 'current');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data() as UserStreaks;
    }
    return null;
  } catch (error) {
    console.error('Error loading user streaks:', error);
    throw new Error(`Failed to load user streaks: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Interface for monthly check-in data
 */
export interface MonthlyCheckInData {
  date: string; // 'YYYY-MM-DD'
  hasMorning: boolean;
  hasNightly: boolean;
  morningCheckIn?: CheckIn;
  nightlyReview?: NightlyReview;
}

/**
 * Load check-ins for a specific month
 * Returns a map of date keys to check-in data
 */
export async function getMonthCheckIns(
  userId: string,
  year: number,
  month: number // 0-indexed (0 = January, 11 = December)
): Promise<Map<string, MonthlyCheckInData>> {
  try {
    const { collection, query, where, getDocs } = await import('firebase/firestore');

    // Calculate date range for the month
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0); // Last day of month

    const startKey = formatDateKey(startDate);
    const endKey = formatDateKey(endDate);

    // Query check-ins collection for the date range
    const checkinsRef = collection(db, 'users', userId, 'checkins');
    const q = query(
      checkinsRef,
      where('__name__', '>=', startKey),
      where('__name__', '<=', endKey)
    );

    const querySnapshot = await getDocs(q);
    const checkInsMap = new Map<string, MonthlyCheckInData>();

    querySnapshot.forEach((doc) => {
      const dateKey = doc.id;
      const data = doc.data();

      checkInsMap.set(dateKey, {
        date: dateKey,
        hasMorning: !!data.morning,
        hasNightly: !!data.nightly,
        morningCheckIn: data.morning as CheckIn | undefined,
        nightlyReview: data.nightly as NightlyReview | undefined,
      });
    });

    console.log(`Loaded ${checkInsMap.size} check-ins for ${year}-${month + 1}`);
    return checkInsMap;
  } catch (error) {
    console.error('Error loading month check-ins:', error);
    throw new Error(`Failed to load month check-ins: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}
